import React, { useState, useEffect } from 'react';
import {
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  GraduationCap,
  Award,
  CheckCircle,
  Globe,
  Code,
  Calendar,
} from 'lucide-react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { firebaseApp } from '../../firebase.js';

const ResumePreviewModal = ({
  candidateId,
  candidateName,
  isOpen,
  onClose,
}) => {
  const [resumeData, setResumeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const db = getFirestore(firebaseApp);

  useEffect(() => {
    if (!isOpen || !candidateId) return;

    const fetchResumeData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch from userProfiles collection
        const profileDoc = await getDoc(doc(db, 'userProfiles', candidateId));

        if (profileDoc.exists()) {
          const data = profileDoc.data();
          setResumeData(data.resumeData || null);
        } else {
          setError('Resume data not found');
        }
      } catch (err) {
        console.error('Error fetching resume data:', err);
        setError('Failed to load resume data');
      } finally {
        setLoading(false);
      }
    };

    fetchResumeData();
  }, [candidateId, isOpen, db]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-xl flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Resume Preview</h2>
            <p className="text-sm text-gray-600 mt-1">{candidateName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading resume...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-600 mb-2">
                <X className="w-12 h-12 mx-auto" />
              </div>
              <p className="text-gray-600">{error}</p>
              <p className="text-sm text-gray-500 mt-2">
                The candidate may not have uploaded a resume yet.
              </p>
            </div>
          ) : !resumeData ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-2">
                <User className="w-12 h-12 mx-auto" />
              </div>
              <p className="text-gray-600">No resume data available</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Personal Info */}
              {resumeData.personalInfo && (
                <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <User className="w-5 h-5 mr-2 text-red-600" />
                    Personal Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {resumeData.personalInfo.name && (
                      <div className="flex items-center text-gray-700">
                        <User className="w-4 h-4 mr-2 text-gray-500" />
                        <span className="font-medium">Name:</span>
                        <span className="ml-2">{resumeData.personalInfo.name}</span>
                      </div>
                    )}
                    {resumeData.personalInfo.email && (
                      <div className="flex items-center text-gray-700">
                        <Mail className="w-4 h-4 mr-2 text-gray-500" />
                        <span className="font-medium">Email:</span>
                        <span className="ml-2">{resumeData.personalInfo.email}</span>
                      </div>
                    )}
                    {resumeData.personalInfo.phone && (
                      <div className="flex items-center text-gray-700">
                        <Phone className="w-4 h-4 mr-2 text-gray-500" />
                        <span className="font-medium">Phone:</span>
                        <span className="ml-2">{resumeData.personalInfo.phone}</span>
                      </div>
                    )}
                    {resumeData.personalInfo.location && (
                      <div className="flex items-center text-gray-700">
                        <MapPin className="w-4 h-4 mr-2 text-gray-500" />
                        <span className="font-medium">Location:</span>
                        <span className="ml-2">{resumeData.personalInfo.location}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Professional Summary */}
              {resumeData.professionalInfo?.summary && (
                <div className="bg-blue-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Briefcase className="w-5 h-5 mr-2 text-blue-600" />
                    Professional Summary
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {resumeData.professionalInfo.summary}
                  </p>
                </div>
              )}

              {/* Skills */}
              {resumeData.professionalInfo?.skills &&
                resumeData.professionalInfo.skills.length > 0 && (
                  <div className="bg-green-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Code className="w-5 h-5 mr-2 text-green-600" />
                      Skills
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {resumeData.professionalInfo.skills.map(
                        (skill, index) => (
                          <span
                            key={index}
                            className="px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-medium flex items-center"
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {skill}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                )}

              {/* Work History */}
              {resumeData.workHistory && resumeData.workHistory.length > 0 && (
                <div className="bg-purple-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Briefcase className="w-5 h-5 mr-2 text-purple-600" />
                    Work Experience
                  </h3>
                  <div className="space-y-4">
                    {resumeData.workHistory.map((job, index) => (
                      <div
                        key={index}
                        className="bg-white rounded-lg p-4 border border-purple-200"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              {job.title || job.position}
                            </h4>
                            <p className="text-sm text-gray-600">
                              {job.company || job.organization}
                            </p>
                          </div>
                          {(job.duration || job.dates) && (
                            <div className="flex items-center text-sm text-gray-500">
                              <Calendar className="w-4 h-4 mr-1" />
                              {job.duration || job.dates}
                            </div>
                          )}
                        </div>
                        {job.description && (
                          <p className="text-sm text-gray-700 mt-2">
                            {job.description}
                          </p>
                        )}
                        {job.responsibilities &&
                          Array.isArray(job.responsibilities) && (
                            <ul className="mt-2 space-y-1">
                              {job.responsibilities.map(
                                (resp, idx) => (
                                  <li
                                    key={idx}
                                    className="text-sm text-gray-600 flex items-start"
                                  >
                                    <span className="text-purple-500 mr-2">•</span>
                                    {resp}
                                  </li>
                                )
                              )}
                            </ul>
                          )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Education */}
              {resumeData.professionalInfo?.education && (
                <div className="bg-yellow-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <GraduationCap className="w-5 h-5 mr-2 text-yellow-600" />
                    Education
                  </h3>
                  <p className="text-gray-700">{resumeData.professionalInfo.education}</p>
                </div>
              )}

              {/* Certifications */}
              {resumeData.additionalInfo?.certifications &&
                resumeData.additionalInfo.certifications.length > 0 && (
                  <div className="bg-indigo-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Award className="w-5 h-5 mr-2 text-indigo-600" />
                      Certifications
                    </h3>
                    <ul className="space-y-2">
                      {resumeData.additionalInfo.certifications.map(
                        (cert, index) => (
                          <li
                            key={index}
                            className="flex items-center text-gray-700"
                          >
                            <Award className="w-4 h-4 mr-2 text-indigo-500" />
                            {cert}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                )}

              {/* Languages */}
              {resumeData.additionalInfo?.languages &&
                resumeData.additionalInfo.languages.length > 0 && (
                  <div className="bg-pink-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Globe className="w-5 h-5 mr-2 text-pink-600" />
                      Languages
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {resumeData.additionalInfo.languages.map(
                        (language, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-pink-100 text-pink-800 rounded-full text-sm"
                          >
                            {language}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                )}

              {/* Projects */}
              {resumeData.additionalInfo?.projects &&
                resumeData.additionalInfo.projects.length > 0 && (
                  <div className="bg-teal-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Code className="w-5 h-5 mr-2 text-teal-600" />
                      Projects
                    </h3>
                    <ul className="space-y-2">
                      {resumeData.additionalInfo.projects.map(
                        (project, index) => (
                          <li
                            key={index}
                            className="flex items-start text-gray-700"
                          >
                            <span className="text-teal-500 mr-2 mt-1">•</span>
                            {project}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                )}

              {/* Experience Summary */}
              {resumeData.professionalInfo?.experience && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Total Experience
                    </span>
                    <span className="text-lg font-bold text-gray-900">
                      {resumeData.professionalInfo.experience}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 rounded-b-xl">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
          >
            Close Resume
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResumePreviewModal;

